import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import "./Contact.css";

export default function Contact() {
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    message: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState(null);

  const validate = (v) => {
    const errs = {};
    if (!v.name.trim()) errs.name = "Name is required.";
    if (!v.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim()))
      errs.email = "Enter a valid email.";
    if (!v.message.trim()) errs.message = "Message is required.";
    else if (v.message.trim().length < 10)
      errs.message = "Message should be at least 10 characters.";
    return errs;
  };

  const errors = validate(values);
  const isValid = Object.keys(errors).length === 0;

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((s) => ({ ...s, [name]: value }));
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((s) => ({ ...s, [name]: true }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (!isValid || loading) return;

    setLoading(true);
    setSendError(null);

    // Works with both Vite and CRA:
    const serviceId =
      (typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.VITE_EMAILJS_SERVICE_ID) ||
      process.env.REACT_APP_EMAILJS_SERVICE_ID;

    const templateId =
      (typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID) ||
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

    const publicKey =
      (typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY) ||
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setSendError(
        "Email service is not configured. Please set your EmailJS env variables."
      );
      setLoading(false);
      return;
    }

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          name: values.name,
          email: values.email,
          message: values.message,
          title: "Website contact message",
        },
        publicKey
      );
      setSubmitted(true);
    } catch (err) {
      setSendError(
        "There was an issue sending your message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="contact">
      <div className="contact-inner">
        <section className="contact-left">
          <h1 className="contact-title">Contact me</h1>
          <p className="contact-paragraph">
            I’d be happy to hear from you! Whether you have questions about my
            research, teaching, or projects, feel free to reach out. Please use
            the form to the right or contact me directly via email. I’ll do my
            best to respond promptly.
          </p>

          <ul className="contact-links">
            <li>
              <a href="mailto:marian.melnyk@gmail.com" className="contact-link">
                Email: marian.melnyk[at]gmail.com
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/in/marianmelnyk/"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link linkedin-link"
              >
                LinkedIn
              </a>
            </li>
          </ul>
        </section>

        <section className="contact-right">
          <div className="contact-form-wrapper">
            {submitted ? (
              <div className="contact-success">
                Thank you. Your message has been sent.
              </div>
            ) : (
              <form className="contact-form" onSubmit={onSubmit} noValidate>
                <label className="contact-label" htmlFor="name">
                  Please leave your message below:
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className={`contact-input ${
                    touched.name && errors.name ? "has-error" : ""
                  }`}
                  value={values.name}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="Your name"
                  required
                />
                {touched.name && errors.name && (
                  <div className="contact-error">{errors.name}</div>
                )}

                {/* 
                <label className="contact-label" htmlFor="email">Your email address</label> */}
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`contact-input ${
                    touched.email && errors.email ? "has-error" : ""
                  }`}
                  value={values.email}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="your_email@example.com"
                  required
                />
                {touched.email && errors.email && (
                  <div className="contact-error">{errors.email}</div>
                )}

                {/* <label className="contact-label" htmlFor="message">Your message</label> */}
                <textarea
                  id="message"
                  name="message"
                  className={`contact-textarea ${
                    touched.message && errors.message ? "has-error" : ""
                  }`}
                  value={values.message}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="Your message..."
                  rows={6}
                  required
                />
                {touched.message && errors.message && (
                  <div className="contact-error">{errors.message}</div>
                )}

                {sendError && <div className="contact-error">{sendError}</div>}

                <button
                  type="submit"
                  className="send-button"
                  disabled={!isValid || loading}
                >
                  {loading ? "Sending..." : "Send"}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
